/**
 * TradingView Symbol Mapping Utility
 * Maps market IDs and chart types to TradingView symbols
 * 
 * Format: "EXCHANGE:SYMBOL" or "EXCHANGE:SYMBOL.P" for perpetuals
 * 
 * Common exchanges:
 * - BITSTAMP: Bitcoin, Ethereum
 * - BINANCE: Most altcoins
 * - COINBASE: Major cryptocurrencies
 */

export const TRADINGVIEW_SYMBOLS = {
  // Bitcoin
  'bitcoin': 'BITSTAMP:BTCUSD',
  'btc': 'BITSTAMP:BTCUSD',
  
  // Ethereum
  'ethereum': 'BITSTAMP:ETHUSD',
  'eth': 'BITSTAMP:ETHUSD',
  
  // Cosmos
  'atom': 'BINANCE:ATOMUSD.P',
  'cosmos': 'BINANCE:ATOMUSD.P',
  
  // Solana
  'solana': 'BINANCE:SOLUSD.P',
  'sol': 'BINANCE:SOLUSD.P',
  
  // Ripple
  'ripple': 'BINANCE:XRPUSD.P',
  'xrp': 'BINANCE:XRPUSD.P',
  
  // Litecoin
  'litecoin': 'BINANCE:LTCUSD.P',
  'ltc': 'BINANCE:LTCUSD.P',
  
  // Binance Coin
  'binancecoin': 'BINANCE:BNBUSD.P',
  'bnb': 'BINANCE:BNBUSD.P',
  
  // NEAR Protocol
  'near': 'BINANCE:NEARUSD.P',
  'near-protocol': 'BINANCE:NEARUSD.P',
  
  // Trump
  'trump': 'BINANCE:TRUMPUSDT.P',
  
  // Hype
  'hype': 'BINANCE:HYPEUSDT.P',
  
  // Add more mappings as needed
  // For tokens not available on TradingView, you can use alternative exchanges
  // or create custom price feeds
};

/**
 * Get TradingView symbol for a market
 * @param {string} marketId - Market ID (e.g., 'btc', 'eth')
 * @param {string} chartType - Chart type (e.g., 'bitcoin', 'ethereum')
 * @returns {string|null} TradingView symbol or null if not found
 */
export const getTradingViewSymbol = (marketId, chartType) => {
  // Try marketId first
  if (TRADINGVIEW_SYMBOLS[marketId?.toLowerCase()]) {
    return TRADINGVIEW_SYMBOLS[marketId.toLowerCase()];
  }
  
  // Try chartType
  if (TRADINGVIEW_SYMBOLS[chartType?.toLowerCase()]) {
    return TRADINGVIEW_SYMBOLS[chartType.toLowerCase()];
  }
  
  // Try with symbol prefix (e.g., 'SOL' -> 'sol')
  const symbolKey = marketId?.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (TRADINGVIEW_SYMBOLS[symbolKey]) {
    return TRADINGVIEW_SYMBOLS[symbolKey];
  }
  
  // Default fallback: try to construct from marketId
  // This is a best-effort approach for unknown symbols
  const upperSymbol = marketId?.toUpperCase();
  if (upperSymbol && upperSymbol.length <= 6) {
    // Try BINANCE as default exchange for unknown symbols
    return `BINANCE:${upperSymbol}USD.P`;
  }
  
  return null;
};

/**
 * Check if a TradingView symbol is valid
 * @param {string} symbol - TradingView symbol
 * @returns {boolean} True if symbol format is valid
 */
export const isValidTradingViewSymbol = (symbol) => {
  if (!symbol || typeof symbol !== 'string') return false;
  // Format: EXCHANGE:SYMBOL or EXCHANGE:SYMBOL.P
  return /^[A-Z0-9]+:[A-Z0-9]+(\.[A-Z]+)?$/.test(symbol);
};

